import React, { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { StopSelector } from './StopSelector';
import routesData from '../data/routes.json';
import stopsData from '../data/stops.json';
import type { Route, Stop, StopsData } from '../types/ferry';

interface DepartureBoardPanelProps {
  selectedPort: string | null;
  onPortSelect: (portId: string) => void;
  onPlanRoute: (fromId: string, toId: string) => void;
  onRouteHover?: (routeId: string | null) => void;
}

interface Departure {
  time: string;
  destination: Stop;
  route: Route;
  serviceName: string;
  duration: number;
  days: string[];
}

export const DepartureBoardPanel: React.FC<DepartureBoardPanelProps> = ({
  selectedPort,
  onPortSelect,
  onPlanRoute,
  onRouteHover
}) => {
  const { language, t } = useLanguage();
  const stops = stopsData as StopsData;

  // Get next departures from selected port
  const departures = useMemo(() => {
    if (!selectedPort) return [];

    // Use Japan Standard Time (JST = UTC+9)
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const jstTime = new Date(utc + (3600000 * 9));

    const currentTime = jstTime.getHours() * 60 + jstTime.getMinutes();
    const currentDay = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][jstTime.getDay()];

    const allDepartures: Departure[] = [];

    // Find all routes that include this port
    routesData.forEach((route: Route) => {
      const portIndex = route.stops.findIndex(stop => stop === selectedPort);
      if (portIndex === -1) return; // Port not in route

      // Check all services
      route.services.forEach(service => {
        // Find all schedule keys that start from this port
        Object.keys(service.schedules).forEach(scheduleKey => {
          const keyParts = scheduleKey.split('_to_');
          if (keyParts.length !== 2) return;

          const fromStopId = keyParts[0];

          // Check if this schedule starts from our selected port (handle shortened IDs)
          const fromMatches = fromStopId === selectedPort ||
                             (selectedPort === 'naoshima_miyanoura' && fromStopId === 'naoshima') ||
                             (selectedPort === 'teshima_ieura' && fromStopId === 'teshima') ||
                             (selectedPort === 'shodoshima_tonosho' && fromStopId === 'shodoshima_tonosho') ||
                             (selectedPort.includes('_') && fromStopId === selectedPort.split('_')[0]);

          if (!fromMatches) return;

          const toStopId = keyParts[1];
          let destinationStopId = toStopId;

          // Handle shortened destination IDs
          if (toStopId === 'naoshima' && stops.naoshima_miyanoura) {
            destinationStopId = 'naoshima_miyanoura';
          } else if (toStopId === 'teshima' && stops.teshima_ieura) {
            destinationStopId = 'teshima_ieura';
          }

          const destinationStop = stops[destinationStopId as keyof typeof stops];
          if (!destinationStop) return;

          const schedules = service.schedules[scheduleKey] || [];

          schedules.forEach(schedule => {
            // Check if this schedule runs today
            const runsToday = schedule.days.includes('daily') || schedule.days.includes(currentDay);
            if (!runsToday) return;

            // Parse departure time
            const [hours, minutes] = schedule.departure.split(':').map(Number);
            const departureTime = hours * 60 + minutes;

            // Only include future departures
            if (departureTime > currentTime) {
              // Calculate actual duration from schedule times
              const [depHours, depMinutes] = schedule.departure.split(':').map(Number);
              const [arrHours, arrMinutes] = schedule.arrival.split(':').map(Number);
              const depTime = depHours * 60 + depMinutes;
              const arrTime = arrHours * 60 + arrMinutes;
              const actualDuration = arrTime - depTime;

              allDepartures.push({
                time: schedule.departure,
                destination: destinationStop,
                route,
                serviceName: service.name,
                duration: actualDuration,
                days: schedule.days
              });
            }
          });
        });
      });
    });

    // Sort by time and return first 10
    return allDepartures.sort((a, b) => {
      const [aHours, aMinutes] = a.time.split(':').map(Number);
      const [bHours, bMinutes] = b.time.split(':').map(Number);
      return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
    }).slice(0, 10);
  }, [selectedPort, stops]);

  const getStopName = (stop: Stop) => {
    return t.getStopName(stop.name);
  };

  const selectedStop = selectedPort ? stops[selectedPort as keyof typeof stops] : null;

  return (
    <div className="space-y-4">
      {/* Port Selector */}
      <StopSelector
        stops={stops}
        selectedStop={selectedPort || ''}
        onStopSelect={onPortSelect}
        placeholder={language === 'zh' ? '搜索或选择港口...' : language === 'ja' ? '港を検索または選択...' : 'Search or select port...'}
        label={language === 'zh' ? '出发港口' : language === 'ja' ? '出発港' : 'Departure Port'}
      />

      {/* Departures List */}
      {selectedPort && (
        <div>
          <h3 className={`text-lg font-semibold text-gray-900 mb-3 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}>
            {language === 'zh' ? `从${selectedStop ? getStopName(selectedStop) : ''}出发` :
             language === 'ja' ? `${selectedStop ? getStopName(selectedStop) : ''}から出発` :
             `Departures from ${selectedStop ? getStopName(selectedStop) : ''}`}
          </h3>

          {departures.length === 0 ? (
            <div className={`text-center py-8 text-gray-500 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}>
              {language === 'zh' ? '今天没有更多出发班次' :
               language === 'ja' ? '本日はこれ以上の出発便はありません' :
               'No more departures today'}
            </div>
          ) : (
            <div className="space-y-3">
              {departures.map((departure, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  onMouseEnter={() => onRouteHover?.(departure.route.id)}
                  onMouseLeave={() => onRouteHover?.(null)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-blue-600 font-mono">
                          {departure.time}
                        </span>
                        <span className={`text-sm text-gray-500 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}>
                          {departure.serviceName}
                        </span>
                      </div>

                      <div className={`text-lg font-medium text-gray-900 mb-1 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}>
                        → {getStopName(departure.destination)}
                      </div>

                      <div className={`text-sm text-gray-600 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}>
                        {language === 'zh' ? `时长: ${departure.duration}分钟` :
                         language === 'ja' ? `所要時間: ${departure.duration}分` :
                         `Duration: ${departure.duration} min`}
                      </div>
                    </div>

                    <button
                      onClick={() => onPlanRoute(selectedPort, departure.destination.id)}
                      className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                    >
                      {language === 'zh' ? '规划路线' : language === 'ja' ? 'ルート計画' : 'Plan Route'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
